import find from 'lodash/find';
import includes from 'lodash/includes';
import map from 'lodash/map';
import isNil from 'lodash/isNil';

import {
  SECTION_TYPE_TILES,
  SECTION_TYPE_TEXT,
  SECTION_TYPE_IFRAME_EMBED,
  SECTION_TYPE_PRESENTATION,
  SECTION_TYPE_HTML_PACKAGE,
  SECTION_TYPE_PDF,
  SECTION_TYPE_VIDEOPLAYLIST,
  SECTION_TYPE_SLIDESHOW,
  SECTION_TYPE_JOB_FEED,
  SECTION_TYPE_ACCORDION,
  SECTION_TYPE_TABS
} from 'zoomforth_codesbane_viewer/const/sections.js';



export const getDisplayNameForSectionType = (sectionType)=> {

  switch (sectionType) {

  case SECTION_TYPE_TILES:
    return 'Grid';

  case SECTION_TYPE_TEXT:
    return 'Text';

  case SECTION_TYPE_IFRAME_EMBED:
    return 'Custom Embed';

  case SECTION_TYPE_PRESENTATION:
    return 'Presentation';

  case SECTION_TYPE_HTML_PACKAGE:
    return 'HTML Package';

  case SECTION_TYPE_PDF:
    return 'PDF';

  case SECTION_TYPE_VIDEOPLAYLIST:
    return 'Video Playlist';

  case SECTION_TYPE_SLIDESHOW:
    return 'Slideshow';

  case SECTION_TYPE_JOB_FEED:
    return 'Job Feed';

  case SECTION_TYPE_ACCORDION:
    return 'Accordion';

  case SECTION_TYPE_TABS:
    return 'Tabs';

  default:
    throw new Error(`Unexpected section type; ${sectionType}`);

  }
};


export const getDisplayName = (section)=>
  getDisplayNameForSectionType(section.type);


export const getLongDisplayName = (section)=> {
  switch (section.type) {

  case SECTION_TYPE_TILES:
    return `${section.columnCount} Column Grid`;

  case SECTION_TYPE_TEXT:
    return 'Text';

  case SECTION_TYPE_IFRAME_EMBED:
    return 'Custom Embed';

  case SECTION_TYPE_PRESENTATION:
    return 'Presentation';

  case SECTION_TYPE_HTML_PACKAGE:
    return 'HTML Package';

  case SECTION_TYPE_PDF:
    return 'PDF';

  case SECTION_TYPE_VIDEOPLAYLIST:
    return 'Video Playlist';

  case SECTION_TYPE_SLIDESHOW:
    return 'Slideshow';

  case SECTION_TYPE_JOB_FEED:
    return 'Job Feed';

  case SECTION_TYPE_ACCORDION:
    return 'Accordion';

  case SECTION_TYPE_TABS:
    return 'Tabs';

  default:
    throw new Error(`Unexpected section type; ${section.type}`);

  }
};



export const canGoToContent = (section)=>
  (
    includes([SECTION_TYPE_TILES,SECTION_TYPE_VIDEOPLAYLIST,SECTION_TYPE_SLIDESHOW], section.type)
  );



export const isSingletonContentSection = (section)=> {
  switch (section.type) {

  case SECTION_TYPE_TEXT:
  case 'fullbleed': // TODO: Huh? Is this actually a section type??
  case SECTION_TYPE_PDF:
  case SECTION_TYPE_IFRAME_EMBED:
  case SECTION_TYPE_PRESENTATION:
  case SECTION_TYPE_HTML_PACKAGE:
  case SECTION_TYPE_JOB_FEED:
    return true;

  default:
    return false;

  }

};


export const isSectionCreatorSection = (section)=>
  section.type === 'sectioncreator';


export const isOrderedContentSection = (section)=>{

  switch (section.type) {

  case SECTION_TYPE_VIDEOPLAYLIST:
  case SECTION_TYPE_SLIDESHOW:
  case SECTION_TYPE_ACCORDION:
  case SECTION_TYPE_TABS:
    return true;

  default:
    return false;

  }

};


export const getContentUuids = (section)=> {
  if (isSingletonContentSection(section)) {
    return [section.singletonContentUuid];

  } else if (isGridPositionsContentSection(section)) {
    return map(section.gridPositions, 'uuid');

  } else if (isOrderedContentSection(section)) {
    return [ ...section.orderedContentUuids];

  } else if (isSectionCreatorSection(section)) {
    return [];

  } else {
    throw new Error(`Section has invalid type: ${section.type}`);
  }
};


export const isGridPositionsContentSection = (section)=>
  section.type === SECTION_TYPE_TILES;


export const getGridPositionDataForUuid = (section, searchUuid)=>
  find(section.gridPositions, ({ uuid })=> ( uuid === searchUuid ));


export const isFullWidthSection = (section)=>
  section && !!section.isFullWidth;


export const getHeightValue = (section)=> {
  if (!section) {
    return undefined;
  }
  return section.height;
};


export const getHeightUnit = (section)=> {
  if (!section) {
    return undefined;
  }
  return section.heightUnit ? section.heightUnit : 'px';
};


export const getHeightStyle = (section)=> {
  if (!section) {
    return undefined;
  }

  const heightValue = getHeightValue(section);
  const heightUnit = getHeightUnit(section);

  if (heightValue) {
    return `${heightValue}${heightUnit}`;
  }

  return undefined;
};


export const getTileSpacingValue = (section)=> {
  if (!section || isNil(section.tileSpacing) || section.tileSpacing === '') {
    return null;
  }

  const tileSpacingUnit = getTileSpacingUnit(section);

  if (tileSpacingUnit === 'em') {
    return parseFloat(section.tileSpacing);
  }

  return parseInt(section.tileSpacing);
};


export const getTileSpacingUnit = (section)=> {
  if (!section) {
    return null;
  }

  return section.tileSpacingUnit ? section.tileSpacingUnit : 'px';
};


export const getTileSpacingStyle = (section)=> {
  if (!section) {
    return undefined;
  }

  const tileSpacingValue = getTileSpacingValue(section);
  const tileSpacingUnit = getTileSpacingUnit(section);

  if (!isNil(tileSpacingValue)) {
    return `${tileSpacingValue}${tileSpacingUnit}`;
  }

  return undefined;
};


export const getMarginData = (section)=> {
  if (!section) {
    return {};
  }
  return section.marginData || {};
};


export const getPaddingData = (section)=> {
  if (!section) {
    return {};
  }
  return section.paddingData || {};
};


export const getMarginStyle = (section)=> {
  const { isMarginEnabled, top, right, bottom, left } = getMarginData(section);

  if (!isMarginEnabled) {
    return {};
  }

  return {
    margin: `${top || 0}px ${right || 0}px ${bottom || 0}px ${left || 0}px`
  };
};


export const getPaddingStyle = (section)=> {
  const { isPaddingEnabled, top, right, bottom, left } = getPaddingData(section);

  if (!isPaddingEnabled) {
    return {};
  }

  return {
    padding: `${top || 0}px ${right || 0}px ${bottom || 0}px ${left || 0}px`
  };
};


export const getIsVisibleOnMobile = (section)=>
  section && section.isVisibleOnMobile !== false; // if this isn't explicitly set then we should still display it


export const getIsHidden = (section)=>
  section && !!section.isHidden;


export const getIsHiddenOnMobile = (section)=>
  section && !!section.isHiddenOnMobile;


export const getCssClassesList = (section)=>
  section && section.cssClassesList;


export const getGridCompactType = (section)=> {
  const defaultSetting = 'vertical';

  if (!section) {
    return defaultSetting;
  }

  if (section.type !== SECTION_TYPE_TILES) {
    return null;
  }

  if (section.compactType) {
    return section.compactType;
  }

  return defaultSetting;
};
